import React from "react";
import useForm from "../../../../hooks/useForm";
import Form from "../../../../components/form/Form";
import { useMutation } from "@tanstack/react-query";
import Input from "../../../../components/form/Input";
import DatePicker from "../../../../components/form/DatePicker";
import TimePicker from "../../../../components/form/timePicker/TimePicker";
import LocationSearch from "../../../../components/elements/LocationSearch";
import Select from "../../../../components/form/select/Select";
import Button from "../../../../components/form/Button";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useOrganization } from "../../../../context/OrganizationContext";
import { addTimeToDate, parseTimeString } from "../../../../helpers";
import { createEventByAi } from "../../../../services/eventService";
import { useTranslation } from "react-i18next";

const validation = {
  title: { required: true, message: "Title is required" },
  locationStatus: { required: true, message: "Event type is required" },
  //   excerpt: { required: true, message: "Excerpt is required" },
  //   start_end_date: { required: true, message: "Date is required" },
  //   startTime: { required: true, message: "Start time is required" },
  //   endTime: { required: true, message: "End time is required" },
  //   faqs: {
  //     type: "arrayValidation",
  //     message: "All FAQ fields must be completed",
  //     validationField: ["title", "excerpt"],
  //     required: false,
  //   },
};

const EventManageAi = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    // initialState: {
    //   faqs: [],
    //   artists: [],
    // },
  });

  const {
    mutate: handleEventManage,
    error,
    isPending,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        organization: selectedOrganization || null,
        startDate: val?.start_end_date?.startDate
          ? addTimeToDate(
              val?.start_end_date?.startDate,
              parseTimeString(val?.startTime)
            )
          : null,
        endDate: val?.start_end_date?.endDate
          ? addTimeToDate(
              val?.start_end_date?.endDate,
              parseTimeString(val?.endTime)
            )
          : null,
      };
      delete payload?.start_end_date;
      delete payload?.startTime;
      delete payload?.endTime;
      return await createEventByAi(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        navigate(`/${lang}/user/events/event-manage/${res?.data?.uid}`);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  return (
    <Form
      handleSubmit={handleSubmit}
      onSubmit={handleEventManage}
      className={"pt-10"}
    >
      <div className="space-y-6 mx-auto w-2/3 bg-white p-10 rounded-xl">
        <div>
          <div className="font-semibold text-2xl">
            {t("Create an event with AI")}
          </div>
          <div className="font-medium text-gray-500 text-sm">
            {t(
              "Answer a few questions about your event and our AI creation tool will use internal data to build an event page."
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="font-semibold text-lg">
              {t("What is the name of your event?")}
            </div>
            <div className="font-medium text-gray-500 text-sm">
              {t(
                "This will be your event’s title. Your title will be used to help create your event!"
              )}
            </div>
          </div>
          <Input
            label={t("Event title")}
            inputPlaceholder={t("Enter event title")}
            onChange={handleChange}
            inputName={"title"}
            value={values?.title}
            {...formValidate(errors, "title")}
          />
        </div>
        <div className="relative space-y-4">
          <div className="font-semibold text-lg">
            {t("When does your event start and finish?")}
          </div>
          <div className="flex items-center gap-4">
            <DatePicker
              label={t("Pick a Date Range")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              placeholder={t("Select Date")}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              minDate={new Date()}
              range
              onChange={(val) => {
                setFieldsValue({ start_end_date: val });
              }}
              value={{
                startDate: values?.start_end_date?.startDate,
                endDate: values?.start_end_date?.endDate,
              }}
              {...formValidate(errors, "start_end_date")}
            />
            <TimePicker
              label={t("Start Time")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              value={values?.startTime}
              onChange={(time) => {
                handleChange({
                  target: { name: "startTime", value: time },
                });
              }}
              {...formValidate(errors, "startTime")}
            />
            <TimePicker
              label={t("End Time")}
              labelClasses={"!text-lg !text-deep-purple !mb-2"}
              inputClassName={
                "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
              }
              value={values?.endTime}
              onChange={(time) => {
                handleChange({
                  target: { name: "endTime", value: time },
                });
              }}
              {...formValidate(errors, "endTime")}
            />
          </div>
        </div>
        <div className="relative">
          <Select
            label={t("Type of Event")}
            placeholder={t("Select event type")}
            labelClasses={"!text-lg !text-deep-purple !mb-2"}
            dropdownClass="w-full"
            dropdownButtonClass={
              "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
            }
            dropdownData={[
              { name: "Online", value: "online" },
              { name: "Offline", value: "offline" },
            ]}
            onChange={(val) => {
              handleChange({
                target: { name: "locationStatus", value: val },
              });
            }}
            selectedValue={values?.locationStatus}
            {...formValidate(errors, "locationStatus")}
          />
        </div>
        {values?.locationStatus && values?.locationStatus === "offline" && (
          <div className="relative space-y-4">
            <div className="font-semibold text-lg">
              {t("Where is it located?")}
            </div>
            <LocationSearch
              value={values?.address?.name || ""}
              onLocationSelect={(val) => {
                handleChange({
                  target: {
                    name: "address",
                    value: {
                      ...val?.address,
                      name: val?.display_name,
                      coordinates: [val?.lat, val?.lon],
                    },
                  },
                });
              }}
            />
          </div>
        )}
        <div className="flex justify-end">
          <Button
            buttonType="submit"
            buttonLabel={t("Create event")}
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </div>
    </Form>
  );
};

export default EventManageAi;
